import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import {
    Embed,
    Link,
    NewEmbed,
    NewLink,
    NewVideo,
    UnityEmbed,
    UnityLink,
    UnityVideo,
    Video,
} from '@@api/services/externalContent/schemas';
import {
    deserializeEmbed,
    deserializeLink,
    deserializeVideo,
    serializeEmbed,
    serializeLink,
    serializeNewEmbed,
    serializeNewLink,
    serializeNewVideo,
    serializeVideo,
} from '@@api/services/externalContent/transformers';
import config from '@@config';

export const externalContentService = defineService(
    (defineEndpoint) => ({
        embed: {
            get: defineEndpoint({
                method: 'GET',
                path: '/embeds/:id',
                pathParams: z.object({
                    id: Embed.shape.id,
                }),
                responses: {
                    200: UnityEmbed.transform(deserializeEmbed),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/embeds',
                body: NewEmbed.transform(serializeNewEmbed),
                responses: {
                    201: UnityEmbed.transform(deserializeEmbed),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/embeds/:id',
                pathParams: z.object({
                    id: Embed.shape.id,
                }),
                body: Embed.transform(serializeEmbed),
                responses: {
                    200: UnityEmbed.transform(deserializeEmbed),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/embeds/:id',
                pathParams: z.object({
                    id: Embed.shape.id,
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        link: {
            get: defineEndpoint({
                method: 'GET',
                path: '/links/:id',
                pathParams: z.object({
                    id: Link.shape.id,
                }),
                responses: {
                    200: UnityLink.transform(deserializeLink),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/links',
                body: NewLink.transform(serializeNewLink),
                responses: {
                    201: UnityLink.transform(deserializeLink),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/links/:id',
                pathParams: z.object({
                    id: Link.shape.id,
                }),
                body: Link.transform(serializeLink),
                responses: {
                    200: UnityLink.transform(deserializeLink),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/links/:id',
                pathParams: z.object({
                    id: Link.shape.id,
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        video: {
            get: defineEndpoint({
                method: 'GET',
                path: '/videos/:id',
                pathParams: z.object({
                    id: Video.shape.id,
                }),
                responses: {
                    200: UnityVideo.transform(deserializeVideo),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/videos',
                body: NewVideo.transform(serializeNewVideo),
                responses: {
                    201: UnityVideo.transform(deserializeVideo),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/videos/:id',
                pathParams: z.object({
                    id: Video.shape.id,
                }),
                body: Video.transform(serializeVideo),
                responses: {
                    200: UnityVideo.transform(deserializeVideo),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/videos/:id',
                pathParams: z.object({
                    id: Video.shape.id,
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.externalContentUrl,
    },
);
